import React, { useState } from "react";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import {
  Boxes,
  Plus,
  Trash2,
  Upload,
  Layers,
  Wand2,
  Scale,
  MapPin,
  Package,
  Eye,
  Image as ImageIcon,
  ArrowRightLeft
} from "lucide-react";
import { IconTextButton, IconButton } from "../../../components/ButtonComponents";
import { StockComponent } from "../../../types/product";
import { uploadComponentImageToServer } from "../../../service/stockImageService";
import { toast } from "react-toastify";

export type ComponentItemForForm = Omit<StockComponent, 'id'> & {
  id?: number | string;
};

interface ProductComponentsTabProps {
  components: ComponentItemForForm[];
  productName: string;
  defaultShelfLocation?: string;
  productId?: number | null;
  onChange: (components: ComponentItemForForm[]) => void;
  onApplyTotalWeight?: (totalWeight: number) => void;
  onOpenLightbox?: (images: string[], index: number) => void;
}

export const ProductComponentsTab: React.FC<ProductComponentsTabProps> = ({
  components,
  productName,
  defaultShelfLocation = "",
  productId,
  onChange,
  onApplyTotalWeight,
  onOpenLightbox,
}) => {
  const [uploadingIndex, setUploadingIndex] = useState<number | null>(null);

  // Yeni bileşen ekle
  const handleAddComponent = () => {
    const newComp: ComponentItemForForm = {
      id: `new-${Date.now()}-${components.length}`,
      name: "",
      quantity: 1,
      weight: 0,
      locations: defaultShelfLocation || "",
      imageUrl: null,
      secondUnitName: null,
      secondUnitMultiplier: null,
    };
    onChange([...components, newComp]);
  };

  // Bileşen güncelle
  const handleUpdateComponent = (index: number, field: keyof ComponentItemForForm, value: any) => {
    const updated = [...components];
    updated[index] = {
      ...updated[index],
      [field]: value,
    };
    onChange(updated);
  };

  // Bileşen sil
  const handleRemoveComponent = (index: number) => {
    const updated = components.filter((_, i) => i !== index);
    onChange(updated);
    toast.info("Bileşen listeden kaldırıldı.");
  };

  // Ürün adından akıllı bileşen ayrıştırma (örn. "15 MM SİYAH SPREY + YÜZÜK SİYAH")
  const handleParseFromName = () => {
    if (!productName || productName.trim().length === 0) {
      toast.warning("Ayrıştırma yapmak için önce ürün adını giriniz.");
      return;
    }

    // '+' veya '/' karakterlerine göre böl
    const parts = productName
      .split(/[+/]/)
      .map((p) => p.trim())
      .filter((p) => p.length > 0);

    if (parts.length <= 1) {
      toast.info("Ürün adında ayrıştırılabilir bir ayraç (+ veya /) bulunamadı.");
      return;
    }

    const newComponents: ComponentItemForForm[] = parts.map((partName, idx) => ({
      id: `new-${Date.now()}-${idx}`,
      name: partName,
      quantity: 1,
      weight: 0,
      locations: defaultShelfLocation || "",
      imageUrl: null,
      secondUnitName: null,
      secondUnitMultiplier: null,
    }));

    onChange(newComponents);
    toast.success(`${newComponents.length} adet bileşen ürün adından oluşturuldu.`);
  };

  // Görsel yükleme
  const handleFileUpload = async (file: File, index: number) => {
    const comp = components[index];
    if (!comp) return;

    if (!comp.id || typeof comp.id !== "number") {
      toast.warning("Görsel yükleyebilmek için lütfen önce ürünü kaydedin.");
      return;
    }

    setUploadingIndex(index);
    try {
      const res = await uploadComponentImageToServer(file, comp.id);
      handleUpdateComponent(index, "imageUrl", res.imageUrl || res.url);
      toast.success("Bileşen görseli başarıyla yüklendi.");
    } catch (err: any) {
      toast.error(err.message || "Görsel yüklenirken bir hata oluştu.");
    } finally {
      setUploadingIndex(null);
    }
  };

  // Toplam hesaplamalar
  const totalWeight = components.reduce((sum, c) => sum + ((c.weight || 0) * (c.quantity || 1)), 0);
  const totalQuantity = components.reduce((sum, c) => sum + (c.quantity || 1), 0);

  return (
    <div className="space-y-4 p-2">
      {/* Üst Başlık & Aksiyon Barı */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-borderColor">
        <div>
          <h3 className="font-bold text-base text-text flex items-center gap-2">
            <Boxes className="w-5 h-5 text-primary" /> Ürün Bileşenleri & Alt Parçalar
          </h3>
          <p className="text-xs text-text opacity-60">
            Set ve çok parçalı ürünlerin depo toplama, tartım ve paketleme operasyonları için alt bileşenlerini yönetin.
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {productName && (productName.includes("+") || productName.includes("/")) && (
            <IconTextButton
              icon={Wand2}
              onClick={handleParseFromName}
              className="btn-secondary text-xs"
              title="Ürün adını '+' veya '/' karakterlerinden ayırarak otomatik bileşen listesi oluşturur."
            >
              İsimden Ayrıştır
            </IconTextButton>
          )}

          <IconTextButton
            icon={Plus}
            onClick={handleAddComponent}
            className="btn-new text-xs"
          >
            Bileşen Ekle
          </IconTextButton>
        </div>
      </div>

      {/* İstatistik & Özet Çubuğu */}
      {components.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="card2 p-3 rounded-xl border border-borderColor flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-primary/10 text-primary">
                <Layers size={16} />
              </div>
              <div className="flex flex-col">
                <span className="text-[10px] uppercase font-bold text-text opacity-60">Bileşen Sayısı</span>
                <span className="text-sm font-black text-text">{components.length} Parça</span>
              </div>
            </div>
            <span className="text-xs font-semibold text-text opacity-75">
              Set Başına {totalQuantity} Adet
            </span>
          </div>

          <div className="card2 p-3 rounded-xl border border-borderColor flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-primary/10 text-primary">
                <Scale size={16} />
              </div>
              <div className="flex flex-col">
                <span className="text-[10px] uppercase font-bold text-text opacity-60">Toplam Set Ağırlığı</span>
                <span className="text-sm font-black text-primary">{totalWeight.toLocaleString("tr-TR")} gr</span>
              </div>
            </div>
            {onApplyTotalWeight && totalWeight > 0 && (
              <button
                type="button"
                onClick={() => onApplyTotalWeight(totalWeight)}
                className="text-[11px] font-bold text-primary hover:underline flex items-center gap-1 transition-all bg-primary/10 px-2 py-1 rounded"
                title="Hesaplanan bu ağırlığı ana ürünün ağırlığına aktarır."
              >
                <ArrowRightLeft size={12} /> Ürüne Aktar
              </button>
            )}
          </div>

          <div className="card2 p-3 rounded-xl border border-borderColor flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-primary/10 text-primary">
                <MapPin size={16} />
              </div>
              <div className="flex flex-col">
                <span className="text-[10px] uppercase font-bold text-text opacity-60">Ana Raf Konumu</span>
                <span className="text-xs font-bold text-text">{defaultShelfLocation || "Tanımlanmamış"}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bileşen Kartları Listesi */}
      {components.length === 0 ? (
        <div className="border-2 border-dashed border-borderColor rounded-2xl p-10 flex flex-col items-center justify-center text-center bg-bgAlt/30 space-y-3">
          <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
            <Boxes size={28} />
          </div>
          <div className="max-w-md">
            <h4 className="font-bold text-sm text-text">Henüz Bileşen Eklenmemiş</h4>
            <p className="text-xs text-text opacity-60 mt-1">
              Tek parça ürünler için bileşen tanımlamak zorunlu değildir. Çok parçalı (sprey, yüzük, kapak, şişe vb.) set ürünler için bileşen ekleyerek depo operasyonlarını kolaylaştırabilirsiniz.
            </p>
          </div>
          <div className="flex items-center gap-2 pt-2">
            {productName && (productName.includes("+") || productName.includes("/")) && (
              <IconTextButton
                icon={Wand2}
                onClick={handleParseFromName}
                className="btn-secondary text-xs"
              >
                İsimden Ayrıştır
              </IconTextButton>
            )}
            <IconTextButton
              icon={Plus}
              onClick={handleAddComponent}
              className="btn-new text-xs"
            >
              İlk Bileşeni Ekle
            </IconTextButton>
          </div>
        </div>
      ) : (
        <div className="space-y-3 max-h-[500px] overflow-y-auto pr-1">
          {components.map((comp, idx) => {
            const isUploading = uploadingIndex === idx;
            const hasImage = Boolean(comp.imageUrl && comp.imageUrl.trim().length > 0);

            return (
              <div
                key={comp.id || idx}
                className="card2 p-4 rounded-2xl border border-borderColor bg-card shadow-xs transition-all hover:border-primary/40 flex flex-col gap-3.5"
              >
                {/* Kart Üst Satırı: Sıra No, Başlık Input ve Sil Butonu */}
                <div className="flex items-center gap-2">
                  <div className="bg-primary/10 text-primary border border-primary/20 px-2.5 py-1 rounded-lg text-xs font-black flex items-center gap-1 shrink-0">
                    <Layers size={13} />
                    <span>#{idx + 1}</span>
                  </div>

                  <div className="flex-1 min-w-0">
                    <InputText
                      value={comp.name || ""}
                      onChange={(e) => handleUpdateComponent(idx, "name", e.target.value)}
                      placeholder="Bileşen Adı (örn: 15 MM SİYAH SPREY)"
                      className="w-full font-bold text-sm"
                    />
                  </div>

                  <IconButton
                    icon={Trash2}
                    onClick={() => handleRemoveComponent(idx)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-500/10 shrink-0"
                    tooltip="Bileşeni Sil"
                  />
                </div>

                {/* Kart İçi Grid Alanları: Miktar, Ağırlık, Konum, 2. Birim */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {/* 1. Miktar */}
                  <div className="flex flex-col gap-1">
                    <label className="text-[11px] font-bold text-text opacity-70 uppercase tracking-wider flex items-center gap-1">
                      <Package size={12} className="text-primary" /> Miktar (Adet)
                    </label>
                    <InputNumber
                      value={comp.quantity ?? 1}
                      onValueChange={(e) => handleUpdateComponent(idx, "quantity", e.value ?? 1)}
                      min={0}
                      showButtons
                      buttonLayout="horizontal"
                      className="w-full"
                    />
                  </div>

                  {/* 2. Ağırlık */}
                  <div className="flex flex-col gap-1">
                    <label className="text-[11px] font-bold text-text opacity-70 uppercase tracking-wider flex items-center gap-1">
                      <Scale size={12} className="text-primary" /> Ağırlık (Gr)
                    </label>
                    <InputNumber
                      value={comp.weight ?? 0}
                      onValueChange={(e) => handleUpdateComponent(idx, "weight", e.value ?? 0)}
                      min={0}
                      minFractionDigits={0}
                      maxFractionDigits={2}
                      suffix=" gr"
                      showButtons
                      buttonLayout="horizontal"
                      className="w-full"
                    />
                  </div>

                  {/* 3. Konum / Raf */}
                  <div className="flex flex-col gap-1">
                    <label className="text-[11px] font-bold text-text opacity-70 uppercase tracking-wider flex items-center gap-1">
                      <MapPin size={12} className="text-primary" /> Raf / Konum
                    </label>
                    <InputText
                      value={comp.locations ?? ""}
                      onChange={(e) => handleUpdateComponent(idx, "locations", e.target.value)}
                      placeholder="örn: B 467"
                      className="w-full text-xs"
                    />
                  </div>

                  {/* 4. İkinci Birim & Çarpan */}
                  <div className="flex flex-col gap-1">
                    <label className="text-[11px] font-bold text-text opacity-70 uppercase tracking-wider">
                      2. Birim & Çarpanı
                    </label>
                    <div className="flex gap-1.5">
                      <InputText
                        value={comp.secondUnitName ?? ""}
                        onChange={(e) => handleUpdateComponent(idx, "secondUnitName", e.target.value)}
                        placeholder="Koli vb."
                        className="w-1/2 text-xs"
                      />
                      <InputNumber
                        value={comp.secondUnitMultiplier ?? null}
                        onValueChange={(e) => handleUpdateComponent(idx, "secondUnitMultiplier", e.value)}
                        placeholder="Çarpan"
                        className="w-1/2 text-xs"
                      />
                    </div>
                  </div>
                </div>

                {/* Kart Alt Satırı: Görsel Önizleme & Yükleme */}
                <div className="bg-bgAlt/50 border border-borderColor/60 p-2.5 rounded-xl flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-3">
                    {hasImage ? (
                      <div
                        onClick={() => onOpenLightbox && onOpenLightbox([comp.imageUrl!], 0)}
                        className="relative group w-10 h-10 rounded-lg overflow-hidden border border-borderColor bg-card cursor-pointer shadow-xs shrink-0"
                        title="Büyütmek için tıklayın"
                      >
                        <img
                          src={comp.imageUrl!}
                          alt={comp.name || "Bileşen"}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white">
                          <Eye size={14} />
                        </div>
                      </div>
                    ) : (
                      <div className="w-10 h-10 rounded-lg border border-dashed border-borderColor flex items-center justify-center text-text opacity-40 shrink-0">
                        <ImageIcon size={18} />
                      </div>
                    )}

                    <div className="flex flex-col">
                      <span className="text-xs font-semibold text-text">
                        {hasImage ? "Bileşen Görseli Tanımlı" : "Bileşen Görseli Yok"}
                      </span>
                      <span className="text-[10px] text-text opacity-50">
                        {hasImage ? "Görseli değiştirmek için yeni dosya seçebilirsiniz." : "WMS görsel kontrolü için fotoğraf ekleyin."}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    {hasImage && (
                      <button
                        type="button"
                        onClick={() => handleUpdateComponent(idx, "imageUrl", null)}
                        className="text-xs text-red-500 hover:text-red-700 px-2 py-1 rounded hover:bg-red-500/10 transition-colors font-medium flex items-center gap-1"
                      >
                        <Trash2 size={13} /> Görseli Kaldır
                      </button>
                    )}

                    <label
                      className={`cursor-pointer text-xs font-semibold flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-borderColor bg-card text-text hover:text-primary hover:border-primary transition-all shadow-xs ${
                        isUploading ? "opacity-60 cursor-not-allowed" : ""
                      }`}
                    >
                      {isUploading ? (
                        <i className="pi pi-spin pi-spinner text-xs" />
                      ) : (
                        <Upload size={13} />
                      )}
                      <span>{isUploading ? "Yükleniyor..." : hasImage ? "Değiştir" : "Görsel Yükle"}</span>
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        disabled={isUploading}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            handleFileUpload(file, idx);
                          }
                          e.target.value = "";
                        }}
                      />
                    </label>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
